import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';

import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { useSpellCorrectionTerms } from '@/pages/Search/SearchPage/hooks/useSpellCorrectionTerms';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { SearchParams } from '@/types/SaveSearch';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { type CollectionConfig, CollectionLayout } from '@/utils/collections/collections.types';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getPriceResultsUrl } from '@/utils/urls';

import { useSearchCardLoading } from '../../hooks/useSearchCardLoading';
import { AuctionHouseSuggestionLink } from '../AuctionHouseSuggestionLink/AuctionHouseSuggestionLink';
import { SpellCorrectionMessage } from '../SpellCorrectionMessage/SpellCorrectionMessage';
import { NoSearchResultsHeader } from './components/NoSearchResultsHeader/NoSearchResultsHeader';
import { RelatedCategories } from './components/RelatedCategories/RelatedCategories';
import { TextPlaceholder } from './components/TextPlaceholder/TextPlaceholder';

const TITLE_PLACEHOLDER_CHARACTER_COUNT = 24;
const COUNT_PLACEHOLDER_CHARACTER_COUNT = 22;

type Props = {
    displayLocation: string;
    displaySearchTerm: string;
    /**
     * Set on a /collections/<slug> page; replaces the whole header with the circle carousel.
     */
    collection?: CollectionConfig;
    hasNoKeywordSearchResults: boolean;
    isArchivedSearch: boolean;
    isLoadingSearchResults: boolean;
    /**
     * The keyword in the URL differs from the one the stored results, count and correction belong to, so none of
     * them can describe it yet. The title and count render as placeholders until the new response arrives.
     */
    isSearchTermChanging: boolean;
    isSEOSearchPage: boolean;
    /**
     * The keyword as the person typed it, from the URL's `originalKeyword` once the page swapped `keyword` for
     * search-party's correction.
     */
    originalSearchTerm?: string;
    saveSearchParams: SearchParams;
    searchTerm: string;
    totalFound: number;
    totalSoldItems: number;
};

export const SearchPageHeader = ({
    collection,
    displayLocation,
    displaySearchTerm,
    hasNoKeywordSearchResults,
    isArchivedSearch,
    isLoadingSearchResults,
    isSearchTermChanging,
    isSEOSearchPage,
    originalSearchTerm,
    saveSearchParams,
    searchTerm,
    totalFound,
    totalSoldItems,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    const priceResultsHref = useLocalizedPath(getPriceResultsUrl(searchTerm));

    const storedSpellCorrection = useSpellCorrectionTerms({ isLoadingSearchResults, originalSearchTerm, searchTerm });
    const spellCorrection = isSearchTermChanging ? undefined : storedSpellCorrection;

    // Manage card loading state for SearchPageCardGrid
    useSearchCardLoading(isLoadingSearchResults, totalFound);

    const { totalFoundRounded, totalSoldItemsRounded } = useMemo(() => {
        return {
            totalFoundRounded: getRoundedNumberText({ totalFound }),
            totalSoldItemsRounded: getRoundedNumberText({ totalFound: totalSoldItems }),
        };
    }, [totalFound, totalSoldItems]);

    const showNoResultsContent = useMemo(() => {
        return hasNoKeywordSearchResults && !isLoadingSearchResults && !isSearchTermChanging;
    }, [hasNoKeywordSearchResults, isLoadingSearchResults, isSearchTermChanging]);

    const renderTitle = () => {
        if (isSearchTermChanging) {
            return (
                <TextPlaceholder
                    characterCount={TITLE_PLACEHOLDER_CHARACTER_COUNT}
                    testid="search-page-header-title-placeholder"
                />
            );
        }
        if (spellCorrection) {
            return formatMessage(['searchPage', 'searchResults', 'correctedTitle'], {
                values: { searchTerm: cleanSearchTerms(spellCorrection.correctedSearchTerm) },
            });
        }
        return formatMessage(['searchPage', 'searchResults', 'title'], { values: { searchTerm: displaySearchTerm } });
    };

    const renderCount = () => {
        if (isSearchTermChanging) {
            return (
                <TextPlaceholder
                    characterCount={COUNT_PLACEHOLDER_CHARACTER_COUNT}
                    testid="search-page-header-count-placeholder"
                />
            );
        }
        if (isArchivedSearch) {
            return formatMessage(['searchPage', 'searchResults', 'soldItemsBody'], {
                values: { totalSoldItems: totalSoldItemsRounded },
            });
        }
        return formatMessage(['searchPage', 'searchResults', 'body'], { values: { totalFoundRounded } });
    };

    if (collection) {
        return (
            <RelatedCategories
                // Only the LAAC-2371 design fills its portholes; the legacy star-wars page ships
                // portrait lot photos that cropping would mangle. Same split as the house
                // exclusions in buildSearchQuery.
                fillTiles={collection.layout === CollectionLayout.Collection}
                heading={collection.subcategoriesHeading}
                subcategories={collection.subcategories}
            />
        );
    }

    if (!searchTerm) {
        return null;
    }

    return (
        <div
            aria-busy={isSearchTermChanging}
            className="pb-md"
            data-testid="search-page-header"
        >
            {!isSEOSearchPage && <AuctionHouseSuggestionLink isLoadingSearchResults={isLoadingSearchResults} />}

            {showNoResultsContent ? (
                <NoSearchResultsHeader
                    displayLocation={displayLocation}
                    displaySearchTerm={displaySearchTerm}
                />
            ) : (
                <>
                    <div
                        className={cn('mb-sm flex flex-wrap items-end gap-x-xs xsMax:flex-col xsMax:items-start', {
                            'mb-xs': isSEOSearchPage,
                        })}
                    >
                        <H1>
                            {renderTitle()}
                            {isSEOSearchPage && displayLocation}
                        </H1>

                        {!isSEOSearchPage && <h2 className="mb-xs hui-text-body-primary">{renderCount()}</h2>}
                    </div>
                    {spellCorrection && (
                        <SpellCorrectionMessage
                            correctedSearchTerm={spellCorrection.correctedSearchTerm}
                            originalSearchTerm={spellCorrection.originalSearchTerm}
                        />
                    )}
                    {isSEOSearchPage && (
                        <>
                            <BodyPrimary className="mr-xs whitespace-normal">
                                {formatMessage(['searchPage', 'seoPage', 'exploreAllResults'], {
                                    values: {
                                        searchTerm: displaySearchTerm,
                                        totalFoundText: (
                                            <span
                                                className="font-bold"
                                                key="totalFoundText"
                                            >
                                                {formatMessage(
                                                    ['searchPage', 'seoPage', 'exploreAllResultsInternalText'],
                                                    { values: { totalFoundRounded } }
                                                )}
                                            </span>
                                        ),
                                    },
                                })}
                                {displayLocation ? `${displayLocation}.` : '.'}
                            </BodyPrimary>
                            <Link
                                aria-label="View price results"
                                href={priceResultsHref}
                            >
                                {formatMessage(['searchPage', 'seoPage', 'viewPriceResults'])}
                            </Link>
                        </>
                    )}
                    {totalFound === 0 && !isSearchTermChanging && (
                        <div className="mb-sm">
                            <BodyPrimary>{formatMessage(['searchPage', 'getNotified', 'body'])}</BodyPrimary>
                        </div>
                    )}
                </>
            )}

            <div className={cn('w-min', { 'mt-sm': isSEOSearchPage })}>
                <SaveSearchContainer
                    saveSearchParams={saveSearchParams}
                    small
                    tooltipPlacement="bottom"
                />
            </div>
        </div>
    );
};
